import React, { useContext, memo } from 'react'
import Tooltip from '../../Tooltip'
import { useSlate } from 'slate-react'
import { cx } from '@emotion/css'

import { isMarkActive, isBlockActive, toggleBlock, toggleMark } from './buttonUtils'
import { EditorProvider } from '../../ContextProvider/EditorProvider'
import { formatShortcut } from '../../../utils/shortcuts/formatShortcut'
//@ts-ignore
import styles from './style.module.css'
import { IDropdownShortcutElementProps } from '../../../utils/types'

const config = {
  placement: 'bottom',
  arrow: false,
  offset: [0, 5],
  delay: [300, 0],
  duration: [0, 0],
  hideOnClick: true,
  theme: 'small'
}

export function DropdownShortcutElement(props: IDropdownShortcutElementProps) {
  const shortcut = formatShortcut(props.shortcut)
  if (shortcut)
    return <span className={styles['dropdown-shortcut']}>{formatShortcut(props.shortcut)}</span>
  else
    return null

}

export declare interface ButtonProps extends React.AllHTMLAttributes<HTMLSpanElement> {
  active: any
  format: any
  shortcut: any
  ref: any,
  dropdown?: boolean,
  isActiveIcon?: boolean,
  activeTitle?: string
}

export const Button = memo(React.forwardRef(
  (
    {
      className,
      active,
      reversed,
      disabled,
      format,
      shortcut,
      title,
      children,
      dropdown = false,
      isActiveIcon = false,
      activeTitle = '',
      ...props
    }: Partial<ButtonProps>,
    ref: React.Ref<HTMLSpanElement>
  ) => {
    return (
      <Tooltip
        config={config}
        content={
          <span className='font-small'>
            {(active && isActiveIcon) ? activeTitle : (title || format)}{' '}
            {shortcut?.key !== undefined && (
              <span
                className={styles['toolbar-shortcut']}>
                {formatShortcut(shortcut.key)}
              </span>
            )}
          </span>
        }>
        <span
          {...props}
          ref={ref}
          data-icon={format}
          className={cx(
            className,
            styles['Button'],
            { [styles['Button--active']]: active && !disabled && !isActiveIcon },
            { [styles['Button--hoverable']]: !((active && !isActiveIcon) || disabled) },
            { [styles['Button--disable']]: disabled },
            { [styles['Button-dropdown']]: dropdown }
          )}>
          <div className={styles['flex-center']}>
            {children}
          </div>
        </span>
      </Tooltip>
    )
  }
), (prev, next) => {
  return prev.disabled === next.disabled && prev.active === next.active
})



export const BlockButton = ({ format, children, title, shortcut }) => {  
  const editor = useSlate()
  const { showEditHtml } = useContext(EditorProvider)
  return (
    <Button
      disabled={showEditHtml && format !== 'Html'}
      active={isBlockActive(editor, format)}
      onMouseDown={(event) => {
        event && event.preventDefault()
        toggleBlock(editor, format)
      }}
      format={format}
      title={title}
      shortcut={shortcut}>
      {children}
    </Button>
  )
}

export const DropdownMenu = ({ format, children }) => {
  const editor = useSlate()
  return (
    <span
      onMouseDown={(event) => {
        event && event.preventDefault()
        toggleBlock(editor, format)
      }}>
      {children}
    </span>
  )
}

export const MarkButton = ({ format, children, shortcut, title }) => {
  const editor = useSlate()
  const { showEditHtml } = useContext(EditorProvider)
  return (
    <Button
      disabled={showEditHtml && format !== 'Html'}
      active={isMarkActive(editor, format)}
      onMouseDown={(event) => {
        event && event.preventDefault();
        toggleMark(editor, format)
      }}
      format={format}
      title={title}
      shortcut={shortcut}>
      {children}
    </Button>
  )
}

export const DropdownMenuItem = (props) => {
  const { icon, title, shortcut, format } = props
  return (
    <React.Fragment>
      <span
        data-icon={format}
        style={{
          display: 'flex',
          alignItems: 'center'
        }}>
        {icon && (
          <span
            style={{
              width: '2rem',
              height: '2rem',
              display: 'flex',
              background: '#F7F9FC',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '1rem',
              borderRadius: '5px'
            }}>
            {icon}
          </span>
        )}
        <span>{title}</span>
      </span>
      {shortcut?.key !== undefined && <DropdownShortcutElement shortcut={shortcut.key} />}
    </React.Fragment>
  )
}
